import React, { useEffect, useState, useMemo, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputLabel,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SignatureField from "../../../components/SignatureField";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import Swal from "sweetalert2";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

// Dark Mode & Input Styles
const headerFieldSx = {
    // Dark mode overrides
    ".dark-theme & .MuiInputBase-input": {
        color: "#fff !important",
    },
    ".dark-theme & .MuiInputBase-root": {
        color: "#fff !important",
    },
    ".dark-theme & .MuiInputLabel-root": {
        color: "rgba(255, 255, 255, 0.7) !important",
    },
    ".dark-theme & .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(255, 255, 255, 0.3) !important",
    },
};

const ARMHSCrisisPlanPreview = ({ patientFormId, data, onClose }) => {
  const [noteData, setNoteData] = useState(null);
  const [formMeta, setFormMeta] = useState(null); // full form record from GetPatientFormsbyId
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [practiceLogo, setPracticeLogo] = useState(null);
  const [practiceName, setPracticeName] = useState("");

  const [patientSigLink, setPatientSigLink] = useState("");

  // const [clientSignatureBase64, setClientSignatureBase64] = useState("");
  // const [clientSignatureURL, setClientSignatureURL] = useState("");
  const sigCanvasRef = useRef(null);
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    useState(false);

  // Removed showSuccessAlert state as we use Swal now

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const resolvedPatientFormId = useMemo(
    () =>
      patientFormId ??
      data?.patientFormId ??
      data?.patientformID ??
      data?.id ??
      data?.Id ??
      null,
    [patientFormId, data]
  );

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  useEffect(() => {
    try {
      const logo =
        typeof window !== "undefined"
          ? localStorage.getItem("practiceLogo1")
          : null;
      if (logo) setPracticeLogo(logo);
    } catch {}
  }, []);

  // Fetch Crisis Plan + Form meta
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      if (!resolvedPatientFormId) {
        if (active) {
          setError("Missing patientFormId");
          setLoading(false);
        }
        return;
      }
      if (!API_BASE_URL) {
        if (active) {
          setError(
            "API base URL is not set. Define NEXT_PUBLIC_API_BASE_URL or REACT_APP_API_URL and restart."
          );
          setLoading(false);
        }
        return;
      }

      const token = localStorage.getItem("neuro_patientportal_auth_token");
      if (!token) {
        if (active) {
          setError("Not authenticated (missing token)");
          setLoading(false);
        }
        return;
      }

      try {
        if (active) {
          setLoading(true);
          setError("");
        }

        const crisisUrl = `${API_BASE_URL}/Rehabilitative/getCrisisPlanById?patientformID=${encodeURIComponent(
          resolvedPatientFormId
        )}`;
        const formUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${encodeURIComponent(
          resolvedPatientFormId
        )}`;

        const [crisisRes, formRes] = await Promise.all([
          fetch(crisisUrl, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(formUrl, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        let crisis = {};
        if (crisisRes.ok && crisisRes.status !== 204) {
          crisis = await crisisRes.json();
        } else if (!crisisRes.ok && crisisRes.status !== 404) {
          const text = await crisisRes.text().catch(() => "");
          throw new Error(text || "Failed to fetch Crisis Plan");
        }

        let formMetaData = {};
        if (formRes.ok && formRes.status !== 204) {
          const formJson = await formRes.json();
          formMetaData = Array.isArray(formJson)
            ? formJson[0] || {}
            : formJson || {};
        }

        const merged = { ...crisis, ...formMetaData };

        if (active) {
          setNoteData(merged || {});
          setFormMeta(formMetaData || {});
          setPracticeName(merged?.practiceName || merged?.particeName || "");
        }
      } catch (err) {
        if (active) {
          setError(err?.message || "Failed to fetch Crisis Plan");
          setNoteData(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();
    return () => {
      active = false;
    };
  }, [resolvedPatientFormId]);

  // Load existing signature (base64 or from URL/path)
  useEffect(() => {
    const token = localStorage.getItem("neuro_patientportal_auth_token");
    if (!token || !API_BASE_URL || !noteData) return;

    const getPreviewLink = async (path) => {
      try {
        if (!path) return "";
        // already a full URL or data URL
        if (/^(https?:|data:)/i.test(path)) return path;

        const res = await fetch(
          `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
            path
          )}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return "";

        const ct = res.headers.get("content-type") || "";
        if (ct.toLowerCase().includes("text/plain")) {
          const link = await res.text();
          return link || "";
        }
        const blob = await res.blob();
        if (blob && blob.size > 0) {
          return URL.createObjectURL(blob);
        }
        return "";
      } catch {
        return "";
      }
    };

    (async () => {
      // 1) Try base64 fields first
      const base64Candidate =
        noteData.clientsBase64 ||
        noteData.clientSignatureBase64 ||
        noteData.clientBase64;

      if (
        base64Candidate &&
        typeof base64Candidate === "string" &&
        base64Candidate.toLowerCase().includes("base64")
      ) {
        setPatientSigLink(base64Candidate);
        return;
      }

      // 2) Fallback to URL/path fields
      const patientPath =
        noteData.patientSignatureURL ||
        noteData.clientsURL ||
        noteData.clientSignatureURL;

      const p = await getPreviewLink(patientPath);
      setPatientSigLink(p);
    })();

    return () => {
      try {
        patientSigLink &&
          patientSigLink.startsWith("blob:") &&
          URL.revokeObjectURL(patientSigLink);
      } catch {}
    };
  }, [noteData]);

  const formatMMDDYYYY = (input) => {
    if (!input) return "";
    if (typeof input === "string") {
      const s = input.trim();
      const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
      if (iso) {
        const [, y, mo, da] = iso;
        const dt = new Date(+y, +mo - 1, +da);
        return Number.isNaN(dt.getTime()) ? "" : `${mo}/${da}/${y}`;
      }
      const mdy = s.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
      if (mdy) {
        const [, mo, da, y] = mdy;
        const dt = new Date(+y, +mo - 1, +da);
        const mm = String(mo).padStart(2, "0");
        const dd = String(da).padStart(2, "0");
        return Number.isNaN(dt.getTime()) ? "" : `${mm}/${dd}/${y}`;
      }
    }
    const dt = new Date(input);
    if (Number.isNaN(dt.getTime())) return "";
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const firstOf = (...vals) =>
    vals.find((v) => v !== undefined && v !== null && String(v).trim() !== "") ||
    "";

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;

    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);

    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) {
          swalEl.style.zIndex = "9999";
        }
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setPatientSigLink("");

      try {
        const token = localStorage.getItem("neuro_patientportal_auth_token");
        const client = axios.create({
          baseURL: API_BASE_URL,
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        let payload;
        if (formMeta) {
          payload = {
            ...formMeta,
            Id: formMeta.Id ?? formMeta.id ?? resolvedPatientFormId,
            patientFormId: resolvedPatientFormId,
            clientsBase64: "",
            clientsURL: "",
            patientSignatureURL: "",
            clientSignatureURL: "",
            imageURLClientSignature: "",
          };
        } else {
          // Fallback minimal payload if formMeta missing
          payload = {
            patientFormId: resolvedPatientFormId,
            clientsBase64: "",
            clientsURL: "",
          };
        }

        await client.post("/Forms/AddPatientForm", payload);

        if (formMeta) {
          setFormMeta((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );
        }
        setNoteData((prev) =>
          prev
            ? {
                ...prev,
                clientsBase64: "",
                clientsURL: "",
                patientSignatureURL: "",
                clientSignatureURL: "",
                imageURLClientSignature: "",
              }
            : prev
        );

        Swal.fire({
          title: "Deleted!",
          text: "Signature has been removed.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) {
              swalEl.style.zIndex = "9999";
            }
          },
        });
      } catch (e) {
        console.error(e);
        Swal.fire({
          title: "Error!",
          text: "Failed to remove signature.",
          icon: "error",
          didOpen: () => {
            const swalEl = document.querySelector(".swal2-container");
            if (swalEl) {
              swalEl.style.zIndex = "9999";
            }
          },
        });
      }
    });
  };

  const handleSaveForm = async () => {
    if (!resolvedPatientFormId) return;

    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const isBase64 =
        patientSigLink &&
        typeof patientSigLink === "string" &&
        patientSigLink.startsWith("data:");

      let payload;
      if (formMeta) {
        payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? resolvedPatientFormId,
          patientFormId: resolvedPatientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
        };
      } else {
        // Fallback minimal payload if formMeta missing
        payload = {
          patientFormId: resolvedPatientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
        };
      }

      await client.post("/Forms/AddPatientForm", payload);

      if (formMeta && isBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }
      if (isBase64) {
        setNoteData((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }

      // --- SweetAlert Success Popup with zIndex fix ---
      Swal.fire({
        title: "Form Saved",
        text: "Form saved successfully",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        didOpen: () => {
          // Ensure popup is above modal
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) swalEl.style.zIndex = "9999";
        },
      }).then(() => {
        // Logic to close the preview form after clicking OK
        if (onClose) {
          onClose();
        } else {
          try {
            const backdrop = document.querySelector(".MuiBackdrop-root");
            if (backdrop) {
              backdrop.click();
            } else {
              const closeButton = document.querySelector('[aria-label="close"]');
              if (closeButton) {
                closeButton.click();
              } else {
                window.history.back();
              }
            }
          } catch (err) {
            console.error("Could not close form:", err);
            window.location.reload();
          }
        }
      });

    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) {
            swalEl.style.zIndex = "9999";
          }
        },
      });
    }
  };

  const note = noteData || {};
  const dobRaw = note.dob || note.dOB || note.DOB || null;
  const fileNo = note.fileNo || note.fileno || note.accountNo || "";
  const phoneVal = note.phoneno || note.phoneNumber || note.phone || "";

  const patientSignedDate = firstOf(
    note.patientSignatureDATE,
    note.clientSignatureDate,
    note.PatientSignedDate
  );

  const staffSignatureMsg = firstOf(
    note.massagestaffSignature,
    note.staffSignatureMessage,
    note.messageStaffSignature
  );
  const staffSignedDate = firstOf(
    note.staffSignatureDATE,
    note.staffsignatureDATE,
    note.staffSignDate
  );

  const internSignatureMsg = firstOf(
    note.messageinternSign,
    note.messageInternSign,
    note.internSignatureMessage
  );
  const internSignedDate = firstOf(
    note.internSignatureDATE,
    note.staffsignatureDATE
  );

  const supervisorSignatureMsg = firstOf(
    note.messageSupervisorSign,
    note.messageSupervisorSignature,
    note.supervisorSignatureMessage
  );
  const supervisorSignedDate = firstOf(
    note.supervisorSignatureDATE,
    note.SupervisorDate
  );

  const directorSignatureMsg = firstOf(
    note.messagetreatmentdirector,
    note.messageTreatmentDirector,
    note.directorSignatureMessage
  );
  const directorSignedDate = firstOf(
    note.directorDate,
    note.treatmentdirectorDate
  );

  const renderSignature = (label, imgLink, signatureMsg, dateVal) => {
    const dateText = formatMMDDYYYY(dateVal);
    const hasSignature = imgLink || signatureMsg || dateText;

    return (
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <Typography
            sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "1rem" } }}
          >
            {label}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          {hasSignature ? (
            imgLink ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <img
                  src={imgLink}
                  alt={`${label} Image`}
                  style={{
                    display: "block",
                    width: 200,
                    height: 80,
                    border: "2px solid #000",
                    backgroundColor: "#f5f5f5",
                    padding: 10,
                    borderRadius: 8,
                    objectFit: "contain",
                  }}
                  className="signature-image"
                />
                <style>{`
                  .dark-theme .signature-image {
                    background-color: #ffffff !important;
                    border: 2px solid #ccc !important;
                  }
                `}</style>

                {dateText && (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.85rem", sm: "1rem" },
                    }}
                  >
                    Signed on {dateText}
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {signatureMsg || (dateText ? `Signed on ${dateText}` : "—")}
              </Typography>
            )
          ) : (
            <Typography color="text.secondary">—</Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography align="center" color="error" sx={{ mt: 4 }}>
        {error}
      </Typography>
    );

  const emergencyContacts = [
    { label: "Team", name: note.teamName, phone: note.teamPhoneNumber },
    {
      label: "Family/Significant other",
      name: note.familyName,
      phone: note.familyPhoneNumber,
    },
    {
      label: "Psychiatrist",
      name: note.psychiatristName,
      phone: note.psychiatristPhoneNumber,
    },
    {
      label: "Primary Care Physician",
      name: note.carePhysicianName,
      phone: note.carePhysicianPhoneNumber,
    },
    {
      label: "Therapist",
      name: note.therapistName,
      phone: note.therapistPhoneNumber,
    },
    {
      label: "Employer",
      name: note.employerName,
      phone: note.employerPhoneNumber,
    },
    { label: "Other", name: note.otherName, phone: note.otherPhoneNumber },
  ];

  return (
    <>
      <Paper
        sx={{
          p: 0,
          mb: 6,
          boxShadow: 3,
          borderRadius: 3,
          bgcolor: "#fff",
          // Dark Mode for Main Container
          ".dark-theme &": {
            bgcolor: "#0e1726",
            color: "#fff",
            boxShadow: "none"
          }
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (theme) => theme.zIndex.appBar,
            bgcolor: "#fff",
            p: { xs: 2, sm: 3, md: 4 },
            pb: 0,
            mb: 3,
            // Dark Mode for Sticky Header
            ".dark-theme &": {
                bgcolor: "#0e1726",
                color: "#fff",
                borderBottom: "1px solid #1f2937"
            }
          }}
        >
          {isMdDown ? (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  mb: 1,
                }}
              >
                <strong>DOS:</strong>{" "}
                {formatMMDDYYYY(note.assessmentDate || note.sessionDate) ||
                  "—"}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                  mb: 1,
                  textAlign: "center",
                }}
              >
                {note.customFormTitle || data?.formTitle || "ARMHS Crisis Plan"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                <strong>DOS:</strong>{" "}
                {formatMMDDYYYY(note.assessmentDate || note.sessionDate) ||
                  "—"}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {note.customFormTitle || data?.formTitle || "ARMHS Crisis Plan"}
              </Typography>
              <Box sx={{ height: 28 }} />
            </Box>
          )}

          {practiceLogo && (
            <Box
              component="img"
              src={practiceLogo}
              alt="Practice Logo"
              sx={{
                width: 65,
                height: 65,
                borderRadius: 1,
                mb: 2,
                mx: "auto",
                display: "block",
              }}
            />
          )}
          <Divider sx={{ mt: isMdDown ? 0 : 0 }} />
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>File #</strong> {fileNo || "—"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "center" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>Client:</strong>{" "}
                {note.patientName ||
                  note.PatientNameclient ||
                  note.clientName ||
                  "—"}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "right" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>DOB:</strong> {formatMMDDYYYY(dobRaw) || "—"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 3 }} />
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <TextField
                label="Title"
                value={note.customFormTitle || ""}
                multiline
                minRows={1}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={headerFieldSx}
              />
              <TextField
                label="Assessment Date"
                value={formatMMDDYYYY(note.assessmentDate)}
                multiline
                minRows={1}
                fullWidth
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={headerFieldSx}
              />
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!note.isPrinted} disabled size="small" />
                }
                label={
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    Printed and Filed
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!note.isChecked} disabled size="small" />
                }
                label={
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    Inactive
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!note.isAuditReady}
                    disabled
                    size="small"
                  />
                }
                label={
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    Audit Ready
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!note.formlock} disabled size="small" />
                }
                label={
                  <Typography
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    Lock
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <Typography
            variant="h5"
            sx={{
              color: "black",
              fontWeight: "bold",
              fontSize: { xs: "1.1rem", sm: "1.5rem" },
              ".dark-theme &": { color: "#fff" }
            }}
          >
            Adult Rehabilitative Mental Health Services Team Crisis Plan
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Client Name:
                </InputLabel>
                <Typography
                  sx={{
                    color: "black",
                    fontWeight: "bold",
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                    ".dark-theme &": { color: "#fff" }
                  }}
                >
                  {note.patientName ||
                    note.PatientNameclient ||
                    note.clientName ||
                    "—"}
                </Typography>
              </Box>
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  File #:
                </InputLabel>
                <Typography
                  sx={{
                    color: "black",
                    fontWeight: "bold",
                    fontSize: { xs: "0.85rem", sm: "1rem" },
                    ".dark-theme &": { color: "#fff" }
                  }}
                >
                  {fileNo || "—"}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  DOB:
                </InputLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  value={formatMMDDYYYY(dobRaw)}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Date of Plan:
                </InputLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  value={formatMMDDYYYY(note.dateOfplan || note.dateOfPlan)}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Address:
                </InputLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  value={note.address || ""}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Phone:
                </InputLabel>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={1}
                  value={phoneVal}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              Directions
            </InputLabel>
            <TextField
              value={note.directions || ""}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Primary Diagnosis
                </InputLabel>
                <TextField
                  value={note.primaryDiagnosis || ""}
                  fullWidth
                  multiline
                  minRows={2}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Medical Diagnosis
                </InputLabel>
                <TextField
                  value={note.medicalDiagnosis || ""}
                  fullWidth
                  multiline
                  minRows={2}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h5"
            sx={{
              color: "black",
              fontWeight: "bold",
              fontSize: { xs: "1rem", sm: "1.5rem" },
              ".dark-theme &": { color: "#fff" }
            }}
          >
            Current Medications
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "black",
              mt: 1,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              ".dark-theme &": { color: "#ccc" }
            }}
          >
            (See list below or attached list.)
          </Typography>

          {note.medications?.length > 0 ? (
            note.medications.map((med, index) => (
              <Paper
                key={index}
                elevation={3}
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
                  mt: 1.5,
                  mb: 1.5,
                  ".dark-theme &": {
                      bgcolor: "#1b2e4b",
                      border: "1px solid #3b3f5c",
                      color: "#fff"
                  }
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  <Box>
                    <InputLabel
                      sx={{ 
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        ".dark-theme &": { color: "#ccc" }
                      }}
                    >
                      Medication
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      value={med.medicationName || ""}
                      InputProps={{ readOnly: true }}
                      sx={headerFieldSx}
                    />
                  </Box>
                  <Box>
                    <InputLabel
                      sx={{ 
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        ".dark-theme &": { color: "#ccc" }
                      }}
                    >
                      Dose
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      value={med.medicationsDose || ""}
                      InputProps={{ readOnly: true }}
                      sx={headerFieldSx}
                    />
                  </Box>
                  <Box>
                    <InputLabel
                      sx={{ 
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        ".dark-theme &": { color: "#ccc" }
                      }}
                    >
                      Prescribed By
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      value={med.prescribedBy || ""}
                      InputProps={{ readOnly: true }}
                      sx={headerFieldSx}
                    />
                  </Box>
                  <Box>
                    <InputLabel
                      sx={{ 
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                        ".dark-theme &": { color: "#ccc" }
                      }}
                    >
                      Purpose
                    </InputLabel>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      value={med.purpose || ""}
                      InputProps={{ readOnly: true }}
                      sx={headerFieldSx}
                    />
                  </Box>
                </Box>
              </Paper>
            ))
          ) : (
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              No Medications Defined
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              Allergies
            </InputLabel>
            <TextField
              value={note.allergies || ""}
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2, ...headerFieldSx }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Pharmacy
                </InputLabel>
                <TextField
                  value={note.pharmacy || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Phone
                </InputLabel>
                <TextField
                  value={note.phone || note.phoneNumber || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
          >
            Persons to Contact in Case of Emergency
          </Typography>

          {emergencyContacts.map((contact, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 2,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                bgcolor: "#f9f9f9",
                ".dark-theme &": {
                    bgcolor: "#1b2e4b",
                    border: "1px solid #3b3f5c",
                    color: "#fff"
                }
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                {contact.label}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <Box>
                  <InputLabel
                    sx={{ 
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      ".dark-theme &": { color: "#ccc" }
                    }}
                  >
                    Name
                  </InputLabel>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    value={contact.name || ""}
                    InputProps={{ readOnly: true }}
                    sx={headerFieldSx}
                  />
                </Box>
                <Box>
                  <InputLabel
                    sx={{ 
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      ".dark-theme &": { color: "#ccc" }
                    }}
                  >
                    Phone Number
                  </InputLabel>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    value={contact.phone || ""}
                    InputProps={{ readOnly: true }}
                    sx={headerFieldSx}
                  />
                </Box>
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Checkbox
                checked={!!note.advanceDirectives}
                readOnly
                size="small"
                sx={{ mr: 1 }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  fontWeight: "bold",
                }}
              >
                Advance Directives
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Directives
                </InputLabel>
                <TextField
                  value={note.directives || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
              <Box>
                <InputLabel
                  sx={{ 
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    ".dark-theme &": { color: "#ccc" }
                  }}
                >
                  Insurance
                </InputLabel>
                <TextField
                  value={note.insurance || ""}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                  sx={headerFieldSx}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
          >
            Client Section
          </Typography>

          <Box sx={{ mt: 1 }}>
            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              Previous suicide attempts and methods
            </InputLabel>
            <TextField
              value={note.previoussuicide || ""}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2, ...headerFieldSx }}
            />

            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              Crisis Precipitators - What makes me feel in crisis?
            </InputLabel>
            <TextField
              value={note.crisisPrecipitators || ""}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2, ...headerFieldSx }}
            />

            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              Coping skills I can use to prevent, cope with, and recover from a
              crisis:
            </InputLabel>
            <TextField
              value={note.copingskills || ""}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2, ...headerFieldSx }}
            />

            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              The team can do the following to help me in crisis:
            </InputLabel>
            <TextField
              value={note.teamHelp || ""}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2, ...headerFieldSx }}
            />

            <InputLabel
              sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
                ".dark-theme &": { color: "#ccc" }
              }}
            >
              Emergency Dependent Care (child, pet, etc.) Arrangements:
            </InputLabel>
            <TextField
              value={note.emergencyDependent || ""}
              fullWidth
              multiline
              minRows={3}
              InputProps={{ readOnly: true }}
              sx={{ mb: 2, ...headerFieldSx }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 1,
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
            }}
          >
            CRISIS COPING STEPS AND OPTIONS
          </Typography>
          <Box sx={{ pl: 1 }}>
            <Typography
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              1. Use my crisis coping plan
            </Typography>
            <Typography
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              2. Call TEAM at {note.callTEAM || ""}
            </Typography>
            <Typography
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              3. Go to the Emergency Room
            </Typography>
            <Typography
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              4. Call 911
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
          >
            Signatures
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Patient Signature:
              </Typography>
            </Grid>
            <Grid item xs={12} sm={9}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {patientSigLink ? (
                  <>
                    <img
                      src={patientSigLink}
                      alt="Patient Signature"
                      style={{
                        display: "block",
                        width: 200,
                        height: 80,
                        border: "2px solid #000",
                        backgroundColor: "#f5f5f5",
                        padding: 10,
                        borderRadius: 8,
                        objectFit: "contain",
                      }}
                      className="signature-image"
                    />
                    <style>{`
                      .dark-theme .signature-image {
                        background-color: #ffffff !important;
                        border: 2px solid #ccc !important;
                      }
                    `}</style>
                    {patientSignedDate && (
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.85rem", sm: "1rem" },
                        }}
                      >
                        Signed on {formatMMDDYYYY(patientSignedDate)}
                      </Typography>
                    )}
                    <Tooltip title="Edit Signature" arrow>
                      <Button
                        onClick={openClientModal}
                        variant="outlined"
                        size="small"
                        style={{ minWidth: "32px", padding: "4px" }}
                        aria-label="Edit Signature"
                      >
                        <EditIcon />
                      </Button>
                    </Tooltip>

                    <Tooltip title="Remove Signature" arrow>
                      <Button
                        onClick={handleDeleteClientSignature}
                        style={{ minWidth: "32px", padding: "4px" }}
                        aria-label="Remove Signature"
                      >
                        <RemoveCircleOutlineIcon style={{ color: "red" }} />
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Link component="button" onClick={openClientModal}>
                    Open signing window
                  </Link>
                )}
              </Box>
            </Grid>
          </Grid>

          {renderSignature(
            "Intern Signature:",
            "",
            internSignatureMsg,
            internSignedDate
          )}
          {renderSignature(
            "Supervisor Signature:",
            "",
            supervisorSignatureMsg,
            supervisorSignedDate
          )}
          {renderSignature(
            "ARMHS Treatment Director:",
            "",
            directorSignatureMsg,
            directorSignedDate
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
        sx={{
            ".dark-theme & .MuiPaper-root": {
                backgroundColor: "#0e1726",
                color: "#fff"
            }
        }}
      >
        <DialogTitle>Add Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1, backgroundColor: "#fff" }}>
           <SignatureField ref={sigCanvasRef} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ARMHSCrisisPlanPreview;